import * as React from "react";
import { Loader2, Activity, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "../../../components/ui-v2/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui-v2/table";
import { Text, MonoText } from "../../../components/ui-v2/typography";
import { WINDOW_OPTIONS, formatMs } from "./constants";

function ResponseTimeTable({ responseTime }) {
  const [sortKey, setSortKey] = React.useState("count");
  const [sortDir, setSortDir] = React.useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  if (!responseTime || Object.keys(responseTime).length === 0) {
    return (
      <div className="py-8 text-center">
        <Text variant="secondary" size="sm">No response time data in this window</Text>
      </div>
    );
  }

  const rows = Object.entries(responseTime)
    .map(([endpoint, stats]) => ({ endpoint, ...stats }))
    .sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });

  const columns = [
    { key: "endpoint", label: "Endpoint", sortable: false },
    { key: "count", label: "Requests" },
    { key: "min", label: "Min" },
    { key: "max", label: "Max" },
    { key: "avg", label: "Avg" },
    { key: "p50", label: "P50" },
    { key: "p95", label: "P95" },
    { key: "p99", label: "P99" },
    { key: "successRate", label: "Success" },
  ];

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={col.sortable !== false ? "cursor-pointer select-none" : ""}
                onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
              >
                <span className="flex items-center">
                  {col.label}
                  {col.sortable !== false && <SortIcon column={col.key} />}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.endpoint}>
              <TableCell>
                <MonoText size="xs">{row.endpoint}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{row.count}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.min)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.max)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.avg)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.p50)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.p95)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText size="xs">{formatMs(row.p99)}</MonoText>
              </TableCell>
              <TableCell className="text-right">
                <MonoText
                  size="xs"
                  className={row.successRate >= 0.95 ? "text-success" : row.successRate >= 0.8 ? "text-warning" : "text-destructive"}
                >
                  {(row.successRate * 100).toFixed(1)}%
                </MonoText>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ResponseTimesTable({ loading, responseTime, windowMinutes }) {
  const windowLabel =
    WINDOW_OPTIONS.find((o) => o.value === windowMinutes)?.label ||
    `${windowMinutes}m`;

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          Response Times
          <Text variant="secondary" size="xs" className="ml-2 font-normal">
            (Last {windowLabel})
          </Text>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {loading ? (
          <div className="h-[200px] flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ResponseTimeTable responseTime={responseTime} />
        )}
      </CardContent>
    </Card>
  );
}

export default ResponseTimesTable;
